import { Licences } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingCardItem } from 'src/components/LoadingBlock/LoadingBlock';
import { CREATE_RESUME_PAGE, LICENCES } from 'src/components/Router';
import { useId } from 'src/components/Router/useId';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { usePage } from 'src/components/Router/usePage';
import { EDIT } from 'src/consts';
import { LICENCES_FORM } from 'src/context/FormContext';
import { analyticsClient } from 'src/modules/analytics';
import { mapExpiryStatusToCertsyStatusAnalytics } from 'src/modules/analytics/certsy';
import { certsyLinkClicked } from 'src/modules/analytics/certsy/actionCreators';
import type {
  CertsyLinkAction,
  CertsyLinkPosition,
} from 'src/modules/analytics/certsy/eventNames';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import {
  getFailedActionsForParent,
  retryParent,
} from 'src/modules/redux-blend';

import { setRouteAsTouched } from '../../CreateResume/actionCreators';
import { EditLicences } from '../EditLicences/EditLicences';
import { useLicences } from '../data';
import type { GraphqlLicence } from '../types';
import { getAnalyticsCustomProps, graphQLLicenceToUILicence } from '../utils';

import translations from './.vocab';

export const ReadLicences = () => {
  const { licences, loading, error } = useLicences();

  const { t } = useTranslations(translations);
  const [drawerTitle, setDrawerTitle] = useState<string>('');

  const blend = useTypedSelector((state) => state.blend);
  const page = usePage();
  const isCreateResumePage = page === 'create-resume-page';

  const paramId = useId();
  const activeLicence = licences.find((licence) => licence.id === paramId);

  const isEmpty = licences.length === 0;

  const dispatch = useDispatch();
  const navigate = useNavigateRoute();

  useEffect(() => {
    if (activeLicence) {
      setDrawerTitle(t('Edit licence or certification'));
    } else {
      setDrawerTitle(t('Add licence or certification'));
    }
  }, [activeLicence, t]);

  const editLicence = (id: string) => {
    analyticsClient.cardOpened({
      currentPageSection: 'licence',
      currentPageSectionState: 'edit',
      customProps: getAnalyticsCustomProps({
        licenceId: activeLicence?.id,
        licences,
      }),
    });

    if (page === CREATE_RESUME_PAGE) {
      setRouteAsTouched(LICENCES);
    }

    navigate({
      card: LICENCES,
      mode: EDIT,
      id,
    });
  };

  const showRetry = getFailedActionsForParent({ blend }, LICENCES).length > 0;
  const handleRetry = () => dispatch(retryParent(LICENCES));

  const cardAction = (
    <CardAction
      card={LICENCES}
      mode={EDIT}
      actionText={t('Add licence or certification')}
      data={{ automation: 'licence-add' }}
      isEmpty={isEmpty}
      onClick={() => {
        analyticsClient.cardOpened({
          currentPageSection: 'licence',
          currentPageSectionState: 'add',
          customProps: getAnalyticsCustomProps({
            licenceId: activeLicence?.id,
            licences,
          }),
        });

        if (page === CREATE_RESUME_PAGE) {
          setRouteAsTouched(LICENCES);
        }
      }}
    />
  );

  const onSeekPassLinkClick = useCallback(
    (
        linkPosition: CertsyLinkPosition,
        licence: GraphqlLicence,
        linkAction: CertsyLinkAction,
      ) =>
      () => {
        if (licence?.credentialType) {
          dispatch(
            certsyLinkClicked({
              linkAction,
              linkPosition,
              ...(licence.verificationUrl
                ? { linkUrl: licence.verificationUrl }
                : {}),
              credentialType: licence.credentialType,
              currentPageSection: 'licence',
              certsyStatus: mapExpiryStatusToCertsyStatusAnalytics(
                licence.credential?.expiryStatus ?? undefined,
              ),
            }),
          );
        }
      },
    [dispatch],
  );

  const description =
    drawerTitle === t('Edit licence or certification')
      ? undefined
      : t('Showcase your licences...');
  const isLoading = Boolean(loading || error);

  return (
    <>
      <Card
        name={LICENCES}
        title={t('Licences & certifications')}
        emptyCardText={t('Showcase your professional credentials...')}
        isEmpty={isEmpty}
        retryAction={showRetry ? handleRetry : undefined}
        cardAction={cardAction}
        showOptionalBadge={isCreateResumePage}
        loading={isLoading}
        loader={
          <Stack space="small">
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        }
      >
        <Licences
          displayVariant="card"
          showExpiryStatus={true}
          licences={licences.map((licence) => ({
            ...graphQLLicenceToUILicence(licence),
            editLicence,
            onVerifyClick: onSeekPassLinkClick('main card', licence, 'verify'),
            onReVerifyClick: onSeekPassLinkClick(
              'main card',
              licence,
              'reverify',
            ),
            onCertsyClick: onSeekPassLinkClick('pop up', licence, 'verify'),
            showCertsyFunctionality: !isCreateResumePage,
          }))}
        />
      </Card>
      <FormDrawer
        title={drawerTitle}
        description={description}
        component={EditLicences}
        card={LICENCES}
        formName={LICENCES_FORM}
      />
    </>
  );
};
